import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';

interface PlayerProps {
  token: string;
  virtualFinishAt?: number;
  initialTime?: number;
  seekTo?: { seconds: number; requestId: number };
  className?: string;
  onCreate?: (player: ReactPlayer) => void;
  onStart?: () => void;
  onPlay?: () => void;
  onFinish?: (origin?: 'virtual' | 'ended') => void;
  onVirtualFinish?: () => void;
  /** Segundos assistidos, reportados a cada tick do player. */
  onProgressSeconds?: (seconds: number) => void;
  onError?: (error: any) => void;
  videoQuality: string
  videoSpeed: string
}

const PlayerVimeo: React.FC<PlayerProps> = ({
  token,
  initialTime = 0,
  seekTo,
  className,
  onFinish,
  onStart,
  onPlay,
  onError,
  onCreate,
  virtualFinishAt = 80,
  onVirtualFinish,
  onProgressSeconds,
  videoQuality,
  videoSpeed
}) => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [firedVirtualFinish, setFiredVirtualFinish] = useState(false);
  const [progress, setProgress] = useState<OnProgressProps>();
  const [isReady, setIsReady] = useState(false);
  const playerRef = useRef<ReactPlayer | null>(null);
  const appliedSeekRequest = useRef<number | null>(null);

  const PLAYBACK_RATE_KEY = 'at:playbackRate';
  const [playbackRate] = useState<number>(() => {
    if(videoSpeed) {
      return parseFloat(videoSpeed)
    }
    const newPlaybackRate = localStorage.getItem(PLAYBACK_RATE_KEY);
    if (newPlaybackRate) {
      return parseFloat(newPlaybackRate);
    }

    return 1;
  });

  const updatePlaybackRate = (newPlaybackRate: number) => {
    localStorage.setItem(PLAYBACK_RATE_KEY, newPlaybackRate.toString());
  };

  useEffect(() => {
    if (token && token.includes('vimeo.com')) {
      setVideoUrl(token);
    } else {
      setVideoUrl(`https://vimeo.com/${token}`);
    }
  }, [token]);

  // https://github.com/CookPete/react-player#responsive-player

  // O initialTime só é aplicado no onReady, então saltos pedidos depois
  // (ex.: timeline da aula) precisam usar a API do player.
  useEffect(() => {
    if (!seekTo) {
      return;
    }

    if (!isReady || !playerRef.current) {
      return;
    }
    if (appliedSeekRequest.current === seekTo.requestId) {
      return;
    }
    appliedSeekRequest.current = seekTo.requestId;
    playerRef.current.seekTo(seekTo.seconds, 'seconds');
  }, [seekTo, isReady]);

  useEffect(() => {
    if (onFinish && virtualFinishAt && !firedVirtualFinish && progress) {
      if (progress.played > virtualFinishAt / 100) {
        setFiredVirtualFinish(true);
        onFinish('virtual');
        onVirtualFinish && onVirtualFinish();
      }
    }
  }, [progress, virtualFinishAt, firedVirtualFinish, onVirtualFinish]);
  return (
    <div className={`relative bg-[#000] ${className ? className : ''}`}>
      <ReactPlayer
        className='react-player absolute left-0 top-0'
        width='100%'
        height='100%'
        url={videoUrl}
        controls={true}
        onProgress={(progress) => {
          setProgress(progress);
          onProgressSeconds && onProgressSeconds(progress.playedSeconds);
        }}
        onEnded={() => {
          onFinish && onFinish('ended');
        }}
        onReady={(player) => {
          playerRef.current = player;
          setIsReady(true);
          onCreate && onCreate(player);

          if (initialTime > 0) {
            player.seekTo(initialTime, 'seconds');
          }
        }}
        onStart={() => {
          onStart && onStart();
        }}
        onPlay={() => {
          onPlay && onPlay();
        }}
        onError={(error) => {
          onError && onError(error);
        }}
        playbackRate={playbackRate}
        onPlaybackRateChange={updatePlaybackRate}
        config={{
          vimeo: {
            playerOptions: {
              quality: videoQuality
            }
          }
        }}
        playsinline={true}
      />
    </div>
  );
};

export default PlayerVimeo;
